"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BRAND_HOME_BRIEFING_SCHEDULABLE_CAPABILITY_IDS = exports.HOME_BRIEFING_STATUSES = exports.BRAND_HOME_BRIEFING_PAYLOAD_SCHEMA = exports.BRAND_HOME_BRIEFING_RECOMMENDATION_SCHEMA = exports.BRAND_HOME_BRIEFING_FRONTEND_AGENT_RECOMMENDATION_SCHEMA = exports.BRAND_HOME_BRIEFING_SCHEDULE_RECOMMENDATION_SCHEMA = exports.BRAND_HOME_BRIEFING_CHAT_RECOMMENDATION_SCHEMA = exports.BRAND_HOME_BRIEFING_LLM_SCHEDULE_RECOMMENDATION_SCHEMA = exports.BRAND_HOME_BRIEFING_LLM_CHAT_RECOMMENDATION_SCHEMA = exports.BRAND_HOME_BRIEFING_RECOMMENDATION_BASE_SCHEMA = exports.BRAND_HOME_BRIEFING_UNDERSTANDING_SCHEMA = exports.BRAND_HOME_BRIEFING_KPI_SCHEMA = exports.BRAND_HOME_BRIEFING_KPIS = exports.BRAND_HOME_BRIEFING_KPI_KEYS = exports.BRAND_HOME_BRIEFING_FACTS_SCHEMA = exports.BRAND_HOME_BRIEFING_FACTS = exports.BRAND_HOME_BRIEFING_FACT_KEYS = exports.BRAND_HOME_BRIEFING_TERMINAL_STEP_STATUSES = exports.BRAND_HOME_BRIEFING_STEP_STATUSES = exports.BRAND_HOME_BRIEFING_LIMITS = void 0;
exports.isBrandHomeBriefingTerminalStepStatus = isBrandHomeBriefingTerminalStepStatus;
exports.isBrandHomeBriefingSchedulableCapability = isBrandHomeBriefingSchedulableCapability;
exports.projectHomeBriefingRecommendation = projectHomeBriefingRecommendation;
const zod_1 = require("zod");
/**
 * Contract for the brand home-page briefing produced by the briefing ingest.
 * Consumed by the backend (LLM output validation + storage) and by the frontend
 * home page. Stored verbatim as the `BrandHomeBriefing.payload` JSON column.
 *
 * Field length is shaped qualitatively via the system prompt and the schema field
 * descriptions (one-line headline, a few-sentence summary, short chips) — NOT enforced as
 * Zod `.max()`: a hard cap makes the whole generation throw `NoObjectGeneratedError` if the
 * model overshoots by a few characters, and presentation/truncation is the frontend's job.
 * Only item counts are bounded, via `BRAND_HOME_BRIEFING_LIMITS` (sliced by the producer).
 */
/** Cardinality caps — how many items get stored/rendered. Enforced by the briefing service (sliced). */
exports.BRAND_HOME_BRIEFING_LIMITS = {
    kpis: 6,
    recommendations: 8,
    /** Terminal steps kept in payload across refreshes; older ones drop off (history stays in memo). */
    terminalStepsCarry: 3,
};
/** Lifecycle for a plan step. Mirrors manager plan step semantics in a lightweight form. */
exports.BRAND_HOME_BRIEFING_STEP_STATUSES = [
    'suggested',
    'in_progress',
    'done',
    'dismissed',
    'scheduled',
];
/** Terminal statuses — steps that leave the active next-steps list. */
exports.BRAND_HOME_BRIEFING_TERMINAL_STEP_STATUSES = [
    'done',
    'dismissed',
    'scheduled',
];
function isBrandHomeBriefingTerminalStepStatus(status) {
    return exports.BRAND_HOME_BRIEFING_TERMINAL_STEP_STATUSES.includes(status);
}
/**
 * Fixed set of facts that can appear as chips in the "what we understood" panel. The LLM fills
 * the value for the keys the report covers and omits the rest — it must not invent new keys.
 */
exports.BRAND_HOME_BRIEFING_FACT_KEYS = [
    'role',
    'brandStage',
    'adsToday',
    'biggestBlocker',
    'monthlySpend',
    'startingChannel',
];
/**
 * Each fixed fact key's chip `label` (frontend owns the icon) and a short `hint` describing what
 * the fact captures — the hint is fed to the LLM so it understands and fills each fact correctly.
 */
exports.BRAND_HOME_BRIEFING_FACTS = {
    role: { label: 'Role', hint: "The user's role / who they are (e.g. Head of growth, Founder)." },
    brandStage: {
        label: 'Brand stage',
        hint: 'Where the brand is in its journey (e.g. early, validated, established, scaling).',
    },
    adsToday: {
        label: 'Ads today',
        hint: 'How ads are run today (e.g. an agency runs them, boosted posts, none yet).',
    },
    biggestBlocker: {
        label: 'Biggest blocker',
        hint: 'The main thing holding growth back (e.g. a scaling ceiling, no creative testing).',
    },
    monthlySpend: {
        label: 'Monthly spend',
        hint: 'Current monthly ad spend or budget range (e.g. $5k–$25k / mo).',
    },
    startingChannel: {
        label: 'Starting channel',
        hint: 'The first channel to focus on (e.g. Meta Ads, Google Ads, TikTok Ads).',
    },
};
/**
 * Facts shape: a fixed object with one OPTIONAL value per fact key. The LLM sets a field only
 * when the report covers it and leaves the rest unset; unknown keys are impossible.
 */
exports.BRAND_HOME_BRIEFING_FACTS_SCHEMA = zod_1.z.object(Object.fromEntries(exports.BRAND_HOME_BRIEFING_FACT_KEYS.map((k) => [k, zod_1.z.string().optional().describe(exports.BRAND_HOME_BRIEFING_FACTS[k].hint)])));
/**
 * Fixed set of KPIs the user can track toward the goal. The LLM emits only the ones the report
 * states a target/intent for, with the value pulled from the report — it must not invent keys.
 */
exports.BRAND_HOME_BRIEFING_KPI_KEYS = [
    'blendedRoas',
    'mer',
    'cac',
    'cpa',
    'ctr',
    'hookRate',
    'cpm',
    'conversionRate',
    'winningCreativesPerWeek',
    'creativesPerWeek',
    'timeToFirstAd',
    'monthlySpend',
    'aov',
];
/** Each KPI key's card `label` (frontend owns the icon) and a `hint` describing what it measures. */
exports.BRAND_HOME_BRIEFING_KPIS = {
    blendedRoas: { label: 'Blended ROAS', hint: 'Blended return on ad spend (revenue ÷ spend across channels).' },
    mer: { label: 'MER', hint: 'Marketing efficiency ratio (total revenue ÷ total marketing spend).' },
    cac: { label: 'CAC', hint: 'Customer acquisition cost.' },
    cpa: { label: 'CPA', hint: 'Cost per acquisition / conversion.' },
    ctr: { label: 'CTR', hint: 'Click-through rate.' },
    hookRate: { label: 'Hook rate', hint: 'Share of viewers still watching after ~3s (thumb-stop).' },
    cpm: { label: 'CPM', hint: 'Cost per 1,000 impressions.' },
    conversionRate: { label: 'Conversion rate', hint: 'Share of clicks/visits that convert.' },
    winningCreativesPerWeek: {
        label: 'Winning creatives / wk',
        hint: 'Validated winning creatives shipped per week.',
    },
    creativesPerWeek: { label: 'Creatives / wk', hint: 'New creatives produced or tested per week.' },
    timeToFirstAd: { label: 'Time to first ad', hint: 'Time from brief to a live ad.' },
    monthlySpend: { label: 'Monthly spend', hint: 'Target monthly ad spend.' },
    aov: { label: 'AOV', hint: 'Average order value.' },
};
/** A single KPI card: a fixed key plus the target value and an optional qualifier. */
exports.BRAND_HOME_BRIEFING_KPI_SCHEMA = zod_1.z.object({
    key: zod_1.z
        .enum(exports.BRAND_HOME_BRIEFING_KPI_KEYS)
        .describe('Which KPI this is — one of the fixed keys. Do not invent keys.'),
    value: zod_1.z.string().describe('The target value as shown, e.g. "3.0×", ">28%", "<1 day", "3+".'),
    caption: zod_1.z
        .string()
        .optional()
        .describe('Short qualifier under the value, e.g. "target · from 2.1× today", "thumb-stop on cold".'),
});
/** Structured "what we understood about your business" summary (the dark panel on the home page). */
exports.BRAND_HOME_BRIEFING_UNDERSTANDING_SCHEMA = zod_1.z.object({
    headline: zod_1.z
        .string()
        .describe('One-line objective in the brand\'s voice, e.g. "Scale what is working on Meta Ads, lifting ROAS".'),
    summary: zod_1.z
        .string()
        .describe('A few sentences: who the user is, the brand and stage, and the primary objective. Plain prose.'),
    facts: exports.BRAND_HOME_BRIEFING_FACTS_SCHEMA.describe('Fixed set of facts; every field is optional. Set a field ONLY when the report clearly states it — never guess. Leave unset any you cannot ground.'),
    kpis: zod_1.z
        .array(exports.BRAND_HOME_BRIEFING_KPI_SCHEMA)
        .optional()
        .describe('KPIs the user will track toward the goal — only those the report states a target or intent for. Pull the value from the report; never fabricate a number. Optional; omit entirely if no targets are stated.'),
});
/** Shared fields on every stored recommendation (assigned by the service, not the LLM). */
exports.BRAND_HOME_BRIEFING_RECOMMENDATION_BASE_SCHEMA = zod_1.z.object({
    id: zod_1.z.string().describe('Stable step id assigned by the service.'),
    status: zod_1.z
        .enum(exports.BRAND_HOME_BRIEFING_STEP_STATUSES)
        .describe('Lifecycle status. Terminal: done, dismissed, scheduled.'),
    title: zod_1.z.string().describe('Short action label, e.g. "Create 5 UGC video ads".'),
    rationale: zod_1.z
        .string()
        .describe('Very short one-line reason this is worth doing now, grounded in the status quo. Aim for under ~90 characters — a single fragment, not a full sentence with clauses.'),
    capabilityId: zod_1.z.string().describe('Id of the platform capability this exercises (from the capability manifest).'),
    icon: zod_1.z.string().optional().describe('Optional icon hint aligned to the frontend icon vocabulary.'),
});
/** LLM-emitted chat recommendation (no id/status — assigned on persist). */
exports.BRAND_HOME_BRIEFING_LLM_CHAT_RECOMMENDATION_SCHEMA = zod_1.z.object({
    type: zod_1.z.literal('chat'),
    title: zod_1.z.string().describe('Short action label, e.g. "Create 5 UGC video ads".'),
    rationale: zod_1.z
        .string()
        .describe('Very short one-line reason this is worth doing now, grounded in the status quo. Aim for under ~90 characters — a single fragment, not a full sentence with clauses.'),
    prompt: zod_1.z
        .string()
        .describe('Ready-to-send prompt that opens a new agent chat, written from the user\'s perspective.'),
    capabilityId: zod_1.z.string().describe('Id of the platform capability this exercises (from the capability manifest).'),
    icon: zod_1.z.string().optional().describe('Optional icon hint aligned to the frontend icon vocabulary.'),
});
/** LLM-emitted schedule recommendation (no id/status — assigned on persist). */
exports.BRAND_HOME_BRIEFING_LLM_SCHEDULE_RECOMMENDATION_SCHEMA = zod_1.z.object({
    type: zod_1.z.literal('schedule'),
    title: zod_1.z.string().describe('Short action label, e.g. "Weekly Meta performance check".'),
    rationale: zod_1.z
        .string()
        .describe('Very short one-line reason scheduling this is worth doing now. Aim for under ~90 characters — a single fragment, not a full sentence with clauses.'),
    objective: zod_1.z
        .string()
        .describe('Self-contained objective for an autonomous scheduled run — must include all context the scheduled agent needs.'),
    cadence: zod_1.z
        .string()
        .optional()
        .describe('Suggested cadence in plain language, e.g. "every Monday at 9am" or "daily".'),
    notificationCriteria: zod_1.z
        .object({
        minRuntime: zod_1.z.number().optional(),
        minEngagement: zod_1.z.number().optional(),
        iterationCount: zod_1.z.number().optional(),
    })
        .optional()
        .describe('Optional notification thresholds for monitoring-style schedules.'),
    capabilityId: zod_1.z.string().describe('Id of the platform capability this exercises (from the capability manifest).'),
    icon: zod_1.z.string().optional().describe('Optional icon hint aligned to the frontend icon vocabulary.'),
});
/** Stored chat recommendation. */
exports.BRAND_HOME_BRIEFING_CHAT_RECOMMENDATION_SCHEMA = exports.BRAND_HOME_BRIEFING_RECOMMENDATION_BASE_SCHEMA.extend({
    type: zod_1.z.literal('chat'),
    prompt: zod_1.z
        .string()
        .describe('Ready-to-send prompt that opens a new agent chat, written from the user\'s perspective.'),
});
/** Stored schedule recommendation. */
exports.BRAND_HOME_BRIEFING_SCHEDULE_RECOMMENDATION_SCHEMA = exports.BRAND_HOME_BRIEFING_RECOMMENDATION_BASE_SCHEMA.extend({
    type: zod_1.z.literal('schedule'),
    objective: zod_1.z.string(),
    cadence: zod_1.z.string().optional(),
    notificationCriteria: zod_1.z
        .object({
        minRuntime: zod_1.z.number().optional(),
        minEngagement: zod_1.z.number().optional(),
        iterationCount: zod_1.z.number().optional(),
    })
        .optional(),
});
/**
 * RESERVED — not emitted in v1. Forward-compatible arm for recommendations that the
 * frontend renders as predefined agent actions rather than free-form chats. Enabling
 * it requires a closed `agentId` enum owned by the frontend; until that exists the
 * briefing service must never emit this arm. Kept in the stored/contract union so the
 * persisted shape and the frontend renderer are forward-compatible.
 */
exports.BRAND_HOME_BRIEFING_FRONTEND_AGENT_RECOMMENDATION_SCHEMA = zod_1.z.object({
    type: zod_1.z.literal('frontend_agent'),
    id: zod_1.z.string(),
    status: zod_1.z.enum(exports.BRAND_HOME_BRIEFING_STEP_STATUSES),
    title: zod_1.z.string(),
    rationale: zod_1.z.string(),
    agentId: zod_1.z.string().describe('RESERVED: closed enum owned by the frontend. Unused in v1.'),
    params: zod_1.z.record(zod_1.z.string(), zod_1.z.string()).optional(),
});
/** Full recommendation contract (all arms). The frontend renders against this union. */
exports.BRAND_HOME_BRIEFING_RECOMMENDATION_SCHEMA = zod_1.z.discriminatedUnion('type', [
    exports.BRAND_HOME_BRIEFING_CHAT_RECOMMENDATION_SCHEMA,
    exports.BRAND_HOME_BRIEFING_SCHEDULE_RECOMMENDATION_SCHEMA,
    exports.BRAND_HOME_BRIEFING_FRONTEND_AGENT_RECOMMENDATION_SCHEMA,
]);
/** The complete briefing payload stored on `BrandHomeBriefing.payload` and read by the home page. */
exports.BRAND_HOME_BRIEFING_PAYLOAD_SCHEMA = zod_1.z.object({
    understanding: exports.BRAND_HOME_BRIEFING_UNDERSTANDING_SCHEMA,
    recommendations: zod_1.z.array(exports.BRAND_HOME_BRIEFING_RECOMMENDATION_SCHEMA),
    /** Internal long-term memory; rewritten on each refresh. Optional on legacy rows. */
    memo: zod_1.z.string().optional(),
});
/** GET /home-briefing `data.status` — distinct lifecycle phases for the dashboard. */
exports.HOME_BRIEFING_STATUSES = ['none', 'generating', 'ready', 'revising'];
/** Capabilities eligible for `schedule`-type recommendations (monitor / analysis / research — not launch). */
exports.BRAND_HOME_BRIEFING_SCHEDULABLE_CAPABILITY_IDS = [
    'competitor-research',
    'ad-research',
    'creative-analysis',
    'executive-ad-report',
    'meta-ads-analysis',
    'google-analytics',
    'shopify-analytics',
    'klaviyo-email',
    'scheduling',
    'slack-notifications',
    'static-ads',
    'video-ads',
];
const SCHEDULABLE_CAPABILITY_SET = new Set(exports.BRAND_HOME_BRIEFING_SCHEDULABLE_CAPABILITY_IDS);
function isBrandHomeBriefingSchedulableCapability(capabilityId) {
    return SCHEDULABLE_CAPABILITY_SET.has(capabilityId);
}
/** Maps a stored recommendation to the manager user-facing plan projection shape. */
function projectHomeBriefingRecommendation(recommendation, positionInActiveList) {
    const inputType = recommendation.type === 'schedule' ? 'auto' : 'user-input-required';
    const status = positionInActiveList === 0 ? 'current' : 'upcoming';
    return {
        key: recommendation.id,
        title: recommendation.title,
        subtitle: recommendation.rationale,
        status,
        inputType,
    };
}
